export function transformMarkdownString(markdownStr: string) {
  let currentPage = markdownStr + '\n\n'
  const tableRegex = /(\n|^)([^\n]*\|[^\n]*\n[^\n]*[-:|][^\n]*\n(\s*\|.*\n)*)/g
  let replacePageContent = currentPage
  // 修复表格中有\n导致表格渲染错误的问题
  replacePageContent = fixMarkdownTableV3(replacePageContent)
  // 替换表格中的<br>标签为空格
  replacePageContent = replaceTableBrTags(replacePageContent)
  // 将<br>标签替换为\n\n
  replacePageContent = replacePageContent.replace(/<br>/g, '\n\n')
  // 过滤 [^0]: 开头的段落
  replacePageContent = replacePageContent.replace(/^\[\^0\]:.*\n?/gm, '')
  // 在表格前后添加空行
  replacePageContent = replacePageContent.replace(tableRegex, (match, p1, p2, p3) => {
    return `\n\n${p2.trim()}\n\n`
  })
  // 处理 \$ 和 \$$ 对渲染行内公式的影响
  replacePageContent = replacePageContent.replace(/\\\\\$/g, '&#36;')
  replacePageContent = replacePageContent.replace(/\\\$/g, '&#36;')
  // 给$$公式前后添加空行
  replacePageContent = replacePageContent.replace(
    /\$\$((?:(?!\$\$)[\s\S])*?)\$\$/g,
    (match, formula) => {
      let result = '\n$$\n'
      result += formula.trim()
      result += '\n$$\n'
      return result
    }
  )
  // 处理注脚内容会和下一段文本合在一起的问题
  replacePageContent = protectFootnotes(replacePageContent)
  // 处理非表格内容，将\n替换为\n\n
  replacePageContent = processNonTableContent(replacePageContent)
  // 过滤以四个空格开头的段落，即过滤注脚内容
  replacePageContent = replacePageContent.replace(/^ {4}.*(?:\r?\n)?/gm, '')
  // 将 [^0] 替换为空字符串
  replacePageContent = replacePageContent.replace(/\[\^0\]/g, '')
  return replacePageContent
}

/**
 * 修复markdown表格格式的函数（改进版）
 * 主要解决单元格内容被错误换行导致的表格渲染问题
 * 支持多个不同结构的表格，并正确处理单元格内的列表项
 */
function fixMarkdownTableV3(markdownStr: string) {
  // 1. 更精确的预处理：只合并明显被错误分割的行，保护列表项
  let result = markdownStr

  // 修复被错误分割的表格行，但保护以 "- " 开头的列表项
  // 只在非列表项的情况下进行合并
  result = result.replace(/(\|[^|\n]*[^-\s])\n([^|\n-][^|]*?)(?=\s*\|)/g, '$1 $2')

  // 2. 按表格分组处理
  const lines = result.split('\n')
  const fixedLines = []

  let i = 0
  while (i < lines.length) {
    const line = lines[i]

    // 如果是表格行开始
    if (line.includes('|') && !line.includes('---')) {
      // 动态确定当前表格的列数
      let currentTablePipeCount = 0

      // 找到当前表格的列数
      for (let j = i; j < lines.length; j++) {
        const checkLine = lines[j]
        if (checkLine.includes('|') && !checkLine.includes('---') && checkLine.trim()) {
          if (j > i && (!lines[j - 1].includes('|') || lines[j - 1].includes('---'))) {
            break
          }
          currentTablePipeCount = (checkLine.match(/\|/g) || []).length
          break
        }
        if (!checkLine.includes('|') && checkLine.trim() && !checkLine.includes('---')) {
          break
        }
      }

      // 处理当前表格的所有行
      while (i < lines.length) {
        const currentLine = lines[i]

        if (!currentLine.includes('|')) {
          break
        }

        if (currentLine.includes('---')) {
          fixedLines.push(currentLine)
          i++
          continue
        }

        const pipeCount = (currentLine.match(/\|/g) || []).length

        if (pipeCount >= currentTablePipeCount) {
          // 行已经完整，但需要处理单元格内的换行符
          const processedLine = processCellContent(currentLine)
          fixedLines.push(processedLine)
        } else {
          // 行不完整，需要与后续行合并
          let completeLine = currentLine
          let j = i + 1

          while (j < lines.length) {
            const nextLine = lines[j]

            if (nextLine.startsWith('|')) {
              break
            }

            // 合并行，保持原有的换行符结构
            completeLine += '\n' + nextLine
            j++

            // 检查合并后的行是否完整
            const newPipeCount = (completeLine.match(/\|/g) || []).length
            if (newPipeCount >= currentTablePipeCount) {
              break
            }
          }

          // 处理完整行中的单元格内换行符
          const processedLine = processCellContent(completeLine)
          fixedLines.push(processedLine)
          i = j - 1
        }

        i++
      }
    } else {
      fixedLines.push(line)
      i++
    }
  }

  return fixedLines.join('\n')
}

/**
 * 处理单元格内容中的换行符
 * 将单元格内的换行符替换为空格
 */
function processCellContent(line: string) {
  // 分割表格行为单元格
  const cells = line.split('|')

  // 处理每个单元格（跳过第一个和最后一个空元素）
  for (let i = 1; i < cells.length - 1; i++) {
    // 将单元格内的换行符替换为空格
    cells[i] = cells[i].replace(/\n/g, ' ')
  }

  return cells.join('|')
}

/**
 * 替换表格中的<br>标签为空格
 * 只替换表格行内的标签，保持其他位置的<br>标签不变
 */
function replaceTableBrTags(markdownStr: string) {
  // 按行分割处理
  const lines = markdownStr.split('\n')
  const processedLines = []

  for (const line of lines) {
    // 只对表格行（包含|的行）进行<br>标签替换
    if (line.includes('|')) {
      const processedLine = line
        .replace(/<br>/g, ' ')
        .replace(/<br\/>/g, ' ')
        .replace(/<br \/>/g, ' ')
      processedLines.push(processedLine)
    } else {
      // 非表格行保持原样
      processedLines.push(line)
    }
  }

  return processedLines.join('\n')
}

/**
 * 处理注脚内容会和下一段文本合在一起的问题
 */
function protectFootnotes(text: string): string {
  // 识别脚注区域（从[^数字]: 开始到下一个段落结束）
  return text.replace(
    /(\[\^\d+\]:\s*)([\s\S]*?)(?=\n\n|\[\^\d+\]:|$)/g,
    (match, marker, content) => {
      // 保护脚注内容中的特殊字符
      const protectedContent = content
        // 将URL转换为纯文本（使用反引号包裹）
        .replace(/(https?:\/\/[^\s\n]+)/g, '`$1`')
        // 转义可能被误解的Markdown字符
        .replace(/([*_~`])/g, '\\$1')
        // 保护邮箱地址
        .replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '`$1`')

      return protectedContent
    }
  )
}
/**
 * 处理非表格内容，将\n替换为\n\n
 */
function processNonTableContent(text: string): string {
  const tableRegex = /(\n|^)([^\n]*\|[^\n]*\n[^\n]*[-:|][^\n]*\n(\s*\|.*\n)*)/g
  const parts: string[] = []
  let lastIndex = 0
  let match

  // 找到所有表格
  while ((match = tableRegex.exec(text)) !== null) {
    // 处理表格前的内容
    const beforeTable = text.slice(lastIndex, match.index)
    parts.push(beforeTable.replace(/\n{1}/g, '\n\n'))

    // 保持表格内容不变
    parts.push(match[0])

    lastIndex = tableRegex.lastIndex
  }

  // 处理最后一个表格后的内容
  const afterLastTable = text.slice(lastIndex)
  parts.push(afterLastTable.replace(/\n{1}/g, '\n\n'))

  return parts.join('')
}
